import { queryOptions } from "@tanstack/react-query";
import type { ApiClient } from "@/lib/api";
import { getCoordinatorApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { ToolCatalogEntry } from "../types";
import { catalogKeys } from "./query-keys";

/**
 * The catalog list endpoint returns the Coordinator envelope
 * (`{ data: { items } }`), but tolerate a bare array or a bare `{ items }`
 * too so the query survives shape changes on either side.
 */
type CatalogListResponse =
  | ToolCatalogEntry[]
  | { items?: ToolCatalogEntry[] }
  | { data?: { items?: ToolCatalogEntry[] } | ToolCatalogEntry[] };

const extractEntries = (payload: CatalogListResponse): ToolCatalogEntry[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if ("data" in payload && payload.data != null) {
    const { data } = payload;
    return Array.isArray(data) ? data : (data.items ?? []);
  }
  if ("items" in payload) {
    return payload.items ?? [];
  }
  return [];
};

const fetchCatalog = async ({
  client,
}: {
  client: ApiClient;
}): Promise<ToolCatalogEntry[]> => {
  const { data } = await client.get<CatalogListResponse>("/catalog");
  return extractEntries(data);
};

export const catalogQueryOptions = (scope: OrgProjectScope) => {
  const client = getCoordinatorApi(scope);

  return queryOptions<ToolCatalogEntry[]>({
    queryKey: catalogKeys.list({ scope }),
    queryFn: () => fetchCatalog({ client }),
  });
};
